/*
  Responsive media banner — carousel controller.

  Registered once as the custom element <responsive-media-carousel>. Because the
  element owns its own lifecycle, section load/unload in the Theme Editor is handled
  for free: connectedCallback boots, disconnectedCallback tears every listener,
  timer and observer down. Several instances can live on one page without sharing
  any state, and nothing is written to window beyond the element definition itself.
*/
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('responsive-media-carousel')) return;

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function ResponsiveMediaCarousel() {
    return Reflect.construct(HTMLElement, [], ResponsiveMediaCarousel);
  }
  ResponsiveMediaCarousel.prototype = Object.create(HTMLElement.prototype);
  ResponsiveMediaCarousel.prototype.constructor = ResponsiveMediaCarousel;
  Object.setPrototypeOf(ResponsiveMediaCarousel, HTMLElement);

  var P = ResponsiveMediaCarousel.prototype;

  /* ---------------------------------------------------------------- lifecycle */

  P.connectedCallback = function () {
    if (this._booted) return;             // idempotent: never bind twice
    this._booted = true;

    this.track = this.querySelector('[data-rmb-track]');
    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-rmb-slide]'));
    if (!this.track || !this.slides.length) return;

    this.opts = {
      slider: this.dataset.slider === 'true',
      autoplay: this.dataset.autoplay === 'true',
      interval: parseInt(this.dataset.interval, 10) || 6000,
      transition: this.dataset.transition || 'slide',
      speed: parseInt(this.dataset.speed, 10) || 500,
      loop: this.dataset.loop === 'true',
      pauseHover: this.dataset.pauseHover === 'true',
      pauseFocus: this.dataset.pauseFocus === 'true',
      pauseHidden: this.dataset.pauseHidden === 'true',
      swipe: this.dataset.swipe === 'true',
      waitVideo: this.dataset.waitVideo === 'true',
      pauseOnVideoPlay: this.dataset.pauseOnVideoPlay === 'true'
    };

    this.index = 0;
    this.timer = null;
    this.userPaused = false;
    this.videoHolding = false;
    this.inView = true;
    this._handlers = [];
    this._observers = [];

    this.prevBtn = this.querySelector('[data-rmb-prev]');
    this.nextBtn = this.querySelector('[data-rmb-next]');
    this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-rmb-dot]'));
    this.counter = this.querySelector('[data-rmb-counter-current]');
    this.playToggle = this.querySelector('[data-rmb-playtoggle]');

    this.motion = window.matchMedia ? window.matchMedia(REDUCED) : null;
    this.reduced = !!(this.motion && this.motion.matches);
    if (this.motion) {
      this._onMotion = function () {
        this.reduced = this.motion.matches;
        this.applyMotionPreference();
      }.bind(this);
      if (this.motion.addEventListener) this.motion.addEventListener('change', this._onMotion);
      else if (this.motion.addListener) this.motion.addListener(this._onMotion);
    }

    this.single = this.slides.length < 2;
    if (this.single) this.setAttribute('data-rmb-single', 'true');

    this.applyMotionPreference();
    this.bind();
    this.goTo(0, { immediate: true, silent: true });
    this.observeViewport();
    this.maybeStart();
  };

  P.disconnectedCallback = function () {
    this.stop();
    (this._handlers || []).forEach(function (h) { h.t.removeEventListener(h.e, h.f, h.o); });
    this._handlers = [];
    (this._observers || []).forEach(function (o) { try { o.disconnect(); } catch (e) {} });
    this._observers = [];
    if (this.motion && this._onMotion) {
      if (this.motion.removeEventListener) this.motion.removeEventListener('change', this._onMotion);
      else if (this.motion.removeListener) this.motion.removeListener(this._onMotion);
    }
    this._booted = false;
  };

  P.on = function (target, evt, fn, opts) {
    if (!target) return;
    target.addEventListener(evt, fn, opts);
    this._handlers.push({ t: target, e: evt, f: fn, o: opts });
  };

  /* ------------------------------------------------------------------ binding */

  P.bind = function () {
    var self = this;

    this.on(this.prevBtn, 'click', function (e) { e.preventDefault(); self.userStep(-1); });
    this.on(this.nextBtn, 'click', function (e) { e.preventDefault(); self.userStep(1); });

    this.dots.forEach(function (dot, i) {
      self.on(dot, 'click', function (e) { e.preventDefault(); self.userGoTo(i); });
    });

    this.on(this.playToggle, 'click', function (e) {
      e.preventDefault();
      self.userPaused = !self.userPaused;
      self.syncPlayToggle();
      if (self.userPaused) self.stop(); else self.maybeStart();
    });

    /* keyboard: arrows move, and focus entering the carousel pauses autoplay */
    this.on(this, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.userStep(self.isRTL() ? 1 : -1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.userStep(self.isRTL() ? -1 : 1); }
    });
    if (this.opts.pauseFocus) {
      this.on(this, 'focusin', function () { self.focusHeld = true; self.stop(); });
      this.on(this, 'focusout', function (e) {
        if (self.contains(e.relatedTarget)) return;
        self.focusHeld = false; self.maybeStart();
      });
    }

    if (this.opts.pauseHover) {
      this.on(this, 'mouseenter', function () { self.hoverHeld = true; self.stop(); });
      this.on(this, 'mouseleave', function () { self.hoverHeld = false; self.maybeStart(); });
    }

    if (this.opts.pauseHidden) {
      this._onVis = function () { if (document.hidden) self.stop(); else self.maybeStart(); };
      this.on(document, 'visibilitychange', this._onVis);
    }

    if (this.opts.swipe && !this.single) this.bindSwipe();

    /* video lifecycle */
    this.slides.forEach(function (slide) {
      var v = slide.querySelector('video');
      if (!v) return;
      self.on(v, 'play', function () {
        /* Only a person pressing play should hold the carousel. Playback we start
           ourselves when a slide becomes active is flagged first, so autoplay is
           never mistaken for a manual interaction. */
        if (v.dataset.rmbAuto === '1') { delete v.dataset.rmbAuto; return; }
        if (self.opts.pauseOnVideoPlay) { self.manualVideo = true; self.stop(); }
      });
      self.on(v, 'pause', function () { self.manualVideo = false; });
      self.on(v, 'ended', function () {
        self.manualVideo = false;
        if (self.opts.waitVideo && self.videoHolding && slide === self.slides[self.index]) {
          self.videoHolding = false;
          self.next();
        }
      });
    });

    /* Theme Editor */
    this._onBlockSelect = function (e) {
      var slide = e.target.closest ? e.target.closest('[data-rmb-slide]') : null;
      if (!slide && e.target && e.target.matches && e.target.matches('[data-rmb-slide]')) slide = e.target;
      if (!slide || !self.contains(slide)) return;
      var i = self.slides.indexOf(slide);
      if (i > -1) { self.editorHeld = true; self.stop(); self.goTo(i, { immediate: true }); }
    };
    this._onBlockDeselect = function () { self.editorHeld = false; self.maybeStart(); };
    this.on(document, 'shopify:block:select', this._onBlockSelect);
    this.on(document, 'shopify:block:deselect', this._onBlockDeselect);
    this.on(document, 'shopify:section:select', function (e) {
      if (e.target && e.target.contains && e.target.contains(self)) { self.editorHeld = true; self.stop(); }
    });
    this.on(document, 'shopify:section:deselect', function () { self.editorHeld = false; self.maybeStart(); });
  };

  P.bindSwipe = function () {
    var self = this, x0 = null, y0 = null, dx = 0, dy = 0, locked = null, moved = false;

    this.on(this.track, 'touchstart', function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      dx = dy = 0; locked = null; moved = false;
      self.swipeHeld = true; self.stop();
    }, { passive: true });

    this.on(this.track, 'touchmove', function (e) {
      if (x0 === null) return;
      dx = e.touches[0].clientX - x0;
      dy = e.touches[0].clientY - y0;
      if (locked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (locked === 'x') {
        moved = true;
        if (e.cancelable) e.preventDefault();       // stop the page scrolling sideways
      }
    }, { passive: false });

    this.on(this.track, 'touchend', function () {
      if (x0 === null) return;
      var threshold = Math.max(40, self.offsetWidth * 0.12);
      if (locked === 'x' && Math.abs(dx) > threshold) {
        self.userStep(dx < 0 ? (self.isRTL() ? -1 : 1) : (self.isRTL() ? 1 : -1));
      }
      x0 = y0 = null; locked = null;
      self.swipeHeld = false; self.maybeStart();
    }, { passive: true });

    /* a swipe must never trigger the slide's link */
    this.on(this.track, 'click', function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault(); e.stopPropagation();
    }, true);
  };

  P.observeViewport = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        self.inView = en.isIntersecting;
        if (self.inView) self.maybeStart(); else self.stop();
      });
    }, { threshold: 0.15 });
    io.observe(this);
    this._observers.push(io);
  };

  /* ------------------------------------------------------------------ helpers */

  P.isRTL = function () {
    return (document.documentElement.getAttribute('dir') || getComputedStyle(this).direction) === 'rtl';
  };

  P.applyMotionPreference = function () {
    this.style.setProperty('--rmb-speed', (this.reduced ? 0 : this.opts.speed) + 'ms');
    if (this.reduced) {
      this.stop();
      this.slides.forEach(function (s) {
        var v = s.querySelector('video');
        if (v) { v.removeAttribute('autoplay'); v.autoplay = false; try { v.pause(); } catch (e) {} }
      });
    }
  };

  P.canAutoplay = function () {
    return this.opts.slider && this.opts.autoplay && !this.single && !this.reduced &&
           !this.userPaused && !this.hoverHeld && !this.focusHeld && !this.swipeHeld &&
           !this.editorHeld && !this.manualVideo && this.inView &&
           !(this.opts.pauseHidden && document.hidden);
  };

  P.maybeStart = function () {
    this.stop();
    if (!this.canAutoplay()) return;
    var slide = this.slides[this.index];
    var v = slide && slide.querySelector('video');

    if (this.opts.waitVideo && v && !v.paused && !v.ended) { this.videoHolding = true; return; }

    var custom = slide ? parseInt(slide.getAttribute('data-duration'), 10) : 0;
    var delay = custom > 0 ? custom : this.opts.interval;
    this.timer = window.setTimeout(this.next.bind(this), delay);
  };

  P.stop = function () {
    if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
    this.videoHolding = false;
  };

  P.syncPlayToggle = function () {
    if (!this.playToggle) return;
    var paused = this.userPaused;
    this.playToggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    this.playToggle.setAttribute('aria-label', paused
      ? (this.playToggle.getAttribute('data-label-play') || 'Play slideshow')
      : (this.playToggle.getAttribute('data-label-pause') || 'Pause slideshow'));
    this.playToggle.setAttribute('data-state', paused ? 'paused' : 'playing');
  };

  /* ---------------------------------------------------------------- navigation */

  P.userStep = function (dir) { this.step(dir); this.maybeStart(); };
  P.userGoTo = function (i) { this.goTo(i); this.maybeStart(); };

  P.step = function (dir) {
    var n = this.slides.length, i = this.index + dir;
    if (i < 0) i = this.opts.loop ? n - 1 : 0;
    if (i > n - 1) i = this.opts.loop ? 0 : n - 1;
    this.goTo(i);
  };

  P.next = function () {
    if (!this.opts.loop && this.index === this.slides.length - 1) { this.stop(); return; }
    this.step(1);
    this.maybeStart();
  };

  P.goTo = function (i, o) {
    o = o || {};
    var n = this.slides.length;
    if (i < 0 || i > n - 1) return;
    var prev = this.index;
    this.index = i;

    this.pauseAllVideos(i);

    if (this.opts.transition === 'fade') {
      this.slides.forEach(function (s, k) {
        s.style.opacity = k === i ? '1' : '0';
        s.style.pointerEvents = k === i ? '' : 'none';
      });
    } else {
      var pct = (this.isRTL() ? 1 : -1) * i * 100;
      this.track.style.transitionDuration = (o.immediate || this.reduced) ? '0ms' : this.opts.speed + 'ms';
      this.track.style.transform = 'translate3d(' + pct + '%,0,0)';
    }

    this.slides.forEach(function (s, k) {
      var active = k === i;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-hidden', active ? 'false' : 'true');
      /* keep keyboard focus out of slides nobody can see */
      Array.prototype.forEach.call(s.querySelectorAll('a,button,video,iframe,[tabindex]'), function (el) {
        if (active) {
          if (el.hasAttribute('data-rmb-tabindex')) { el.setAttribute('tabindex', el.getAttribute('data-rmb-tabindex')); el.removeAttribute('data-rmb-tabindex'); }
          else el.removeAttribute('tabindex');
        } else {
          if (!el.hasAttribute('data-rmb-tabindex') && el.hasAttribute('tabindex')) el.setAttribute('data-rmb-tabindex', el.getAttribute('tabindex'));
          el.setAttribute('tabindex', '-1');
        }
      });
    });

    this.dots.forEach(function (d, k) {
      d.classList.toggle('is-active', k === i);
      d.setAttribute('aria-current', k === i ? 'true' : 'false');
    });

    if (this.counter) this.counter.textContent = String(i + 1);

    this.mountSlide(i);
    this.mountSlide(i + 1 < n ? i + 1 : (this.opts.loop ? 0 : i));
    this.activateSlide(i, prev, !!o.silent);
  };

  /* -------------------------------------------------------------- media loading */

  /* create an external-video iframe only when its slide is current or next */
  P.mountSlide = function (i) {
    var slide = this.slides[i];
    if (!slide) return;
    var holder = slide.querySelector('[data-rmb-embed]');
    if (!holder || holder.dataset.mounted === 'true') return;
    /* pick one source only, so a phone never fetches the desktop player */
    var small = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var src = (small && holder.getAttribute('data-src-mobile')) || holder.getAttribute('data-src');
    if (!src) return;
    var f = document.createElement('iframe');
    f.className = 'rmb__media rmb__iframe';
    f.setAttribute('src', src);
    f.setAttribute('title', holder.getAttribute('data-title') || 'Video');
    f.setAttribute('loading', 'lazy');
    f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    f.setAttribute('allowfullscreen', '');
    f.setAttribute('frameborder', '0');
    holder.appendChild(f);
    holder.dataset.mounted = 'true';
  };

  P.pauseAllVideos = function (except) {
    var self = this;
    this.slides.forEach(function (s, k) {
      if (k === except) return;
      var v = s.querySelector('video');
      if (v && !v.paused) { try { v.pause(); v.currentTime = v.currentTime; } catch (e) {} }
      /* external players: postMessage pause where the provider supports it */
      var f = s.querySelector('iframe');
      if (f && f.contentWindow) {
        try {
          if (/youtube/.test(f.src)) f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          else if (/vimeo/.test(f.src)) f.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (e) {}
      }
      self.unmountIfFar(k);
    });
  };

  /* drop iframes that are far from the active slide so no player keeps running */
  P.unmountIfFar = function (k) {
    var d = Math.abs(k - this.index);
    if (d < 2) return;
    var slide = this.slides[k];
    var holder = slide && slide.querySelector('[data-rmb-embed]');
    if (!holder || holder.dataset.mounted !== 'true') return;
    holder.innerHTML = '';
    holder.dataset.mounted = 'false';
  };

  P.activateSlide = function (i, prev, silent) {
    var slide = this.slides[i];
    if (!slide) return;
    var v = slide.querySelector('video');
    if (!v) return;

    /* upgrade preload now that the slide is visible */
    if (v.getAttribute('preload') === 'none') v.setAttribute('preload', 'metadata');

    var wantsAutoplay = v.getAttribute('data-autoplay') === 'true';
    if (!wantsAutoplay || this.reduced) return;
    if (silent && prev === i && v.currentTime > 0) return;

    v.muted = true;                      // browsers only allow muted autoplay
    v.dataset.rmbAuto = '1';             // mark as ours, so the play handler ignores it
    var pr = v.play();
    if (pr && pr.catch) pr.catch(function () { delete v.dataset.rmbAuto; /* refused — poster stays */ });
  };

  window.customElements.define('responsive-media-carousel', ResponsiveMediaCarousel);
})();
